import * as yup from 'yup'

/** An enum defining the acceptable data types that can be used in an action step. */
export enum ActionDataSpec {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  STRING_ARRAY = 'string[]',
  NUMBER_ARRAY = 'number[]',
  BOOLEAN_ARRAY = 'boolean[]',
}

export type ActionDataPrimitive = string | number | boolean
// Runtime data may contain nulls (e.g., lookup arrays with null values), but TypeScript types
// don't track them. Filter nulls at boundaries before passing to code expecting non-null arrays.
export type ActionDataArray = Array<string> | Array<number> | Array<boolean>

/**
 * Type for any data that can be used in an action step.
 */
export type ActionData = ActionDataPrimitive | ActionDataArray

export const stringActionDataSchema = yup.string().defined().strict()
export const numberActionDataSchema = yup.number().required().strict()
export const booleanActionDataSchema = yup.boolean().required().strict()
export const stringArrayActionDataSchema = yup
  .array()
  .of(stringActionDataSchema.nullable())
  .required()
  .strict()
export const numberArrayActionDataSchema = yup
  .array()
  .of(numberActionDataSchema.nullable())
  .required()
  .strict()
export const booleanArrayActionDataSchema = yup
  .array()
  .of(booleanActionDataSchema.nullable())
  .required()
  .strict()

// Plain guards matching yup's string()/number()/boolean() checks exactly; isValidSync probing built an error per miss.
const PRIMITIVE_TYPES = ['string', 'number', 'boolean'] as const

const unbox = (value: unknown): unknown =>
  value instanceof String || value instanceof Number || value instanceof Boolean
    ? value.valueOf()
    : value

const isPrimitiveOf = (type: (typeof PRIMITIVE_TYPES)[number], value: unknown): boolean => {
  const raw = unbox(value)
  return typeof raw === type && (type !== 'number' || !Number.isNaN(raw))
}

/** Type guard for ActionDataPrimitive: a string, a non-NaN number, or a boolean. */
export const isActionDataPrimitive = (value: unknown): value is ActionDataPrimitive =>
  PRIMITIVE_TYPES.some((type) => isPrimitiveOf(type, value))

// Iteration, not .every(): yup validates array holes (as undefined) where every() would skip them.
const everyItem = (items: unknown[], accept: (item: unknown) => boolean): boolean => {
  for (const item of items) {
    if (!accept(item)) {
      return false
    }
  }
  return true
}

/** Type guard for ActionDataArray: one primitive type throughout, nulls allowed, empty allowed. */
export const isActionDataArray = (value: unknown): value is ActionDataArray =>
  Array.isArray(value) &&
  PRIMITIVE_TYPES.some((type) =>
    everyItem(value, (item) => item === null || isPrimitiveOf(type, item)),
  )

/** Type guard for ActionData (primitive or homogeneous primitive array). */
export const isActionData = (value: unknown): value is ActionData =>
  isActionDataPrimitive(value) || isActionDataArray(value)

export const actionDataPrimitiveSchema = yup
  .mixed<ActionDataPrimitive>(isActionDataPrimitive)
  .required()

export const actionDataArraySchema = yup.mixed<ActionDataArray>(isActionDataArray).required()

/** Schema for a valid ActionData value */
export const actionDataSchema = yup.mixed<ActionData>(isActionData).required()
